import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  Select,
  Space,
  Card,
  message,
  Tag,
  Row,
  Col,
  Typography,
  Empty,
  Tooltip,
  Badge,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  UserAddOutlined,
  DeleteOutlined,
  UserOutlined,
} from "@ant-design/icons";
import PageHeader from "../../../components/headers/PageHeader";
import eventService from "../../../services/eventService";
import { useUser } from "../../../context/UserContext";

const { Option } = Select;

const { Text } = Typography;

const ParticipantModal = ({ open, setOpen, onSelect, record }) => {
  const [allParticipants, setAllParticipants] = useState([]);
  const [eventParticipants, setEventParticipants] = useState([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [creatingParticipant, setCreatingParticipant] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();
  const user = useUser();

  const handleCreateParticipant = async (values) => {
    setCreatingParticipant(true);
    try {
      // Validar que record existe y tiene id
      if (!record || !record.id) {
        message.error("No se puede crear participante: evento no válido");
        return;
      }

      const newParticipant = {
        ...values,
        protocol_cause_id:
          record.protocols[0] + "_" + record.causes[0] + "_" + values.id,
        eventId: record.id,
      };

      await eventService.crearParticipante(newParticipant, user?.idToken || "");

      setCreateModalVisible(false);
      form.resetFields();
      message.success("Participante creado exitosamente");

      // Recargar la lista de participantes después de crear uno nuevo
      await obtenerParticipantes();
    } catch (error) {
      console.error("Error al crear participante:", error);
      message.error("Error al crear participante");
    } finally {
      setCreatingParticipant(false);
    }
  };

  const handleAddParticipant = async (participant) => {
    if (!eventParticipants.find((p) => p.id === participant.id)) {
      try {
        // Crear el objeto con la estructura exacta requerida
        const participantEventData = {
          eventId: record.id,
          features: participant.features || "",
          gender: participant.gender || "",
          id: participant.id,
          name: participant.name,
          nationality: participant.nationality || "",
          protocol_cause_id:
            record.protocols[0] + "_" + record.causes[0] + "_" + participant.id,
          role: participant.role || participant.features,
        };

        // Hacer POST para asociar el participante al evento
        await eventService.crearParticipante(
          participantEventData,
          user?.idToken || ""
        );

        // Actualizar el estado local
        setEventParticipants([...eventParticipants, participant]);
        message.success(`${participant.name} agregado al evento`);
      } catch (error) {
        console.error("Error al agregar participante al evento:", error);
        message.error("Error al agregar participante al evento");
      }
    } else {
      message.warning("Este participante ya está en el evento");
    }
  };

  const handleRemoveParticipant = async (participantId) => {
    try {
      // Aquí deberías implementar un servicio para remover el participante del evento
      // Por ahora solo actualizamos el estado local
      // TODO: Implementar eventService.removerParticipante(participantId, eventId, token)

      setEventParticipants(
        eventParticipants.filter((p) => p.id !== participantId)
      );
      message.success("Participante removido del evento");
    } catch (error) {
      console.error("Error al remover participante del evento:", error);
      message.error("Error al remover participante del evento");
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const getFilteredParticipants = () => {
    if (!searchText) return allParticipants;
    return allParticipants.filter(
      (participant) =>
        participant.name.toLowerCase().includes(searchText.toLowerCase()) ||
        participant.id.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  const obtenerParticipantes = useCallback(async () => {
    try {
      const response = await eventService.consultarParticipantes(
        user?.idToken || ""
      );

      if (response?.Items) {
        // 1. PRIMERO: Extraer participantes asociados al evento actual
        if (record && record.id) {
          const eventParticipantsList = response.Items.filter(
            (participant) => participant.eventId === record.id
          ).map((participant) => ({
            ...participant,
            key: participant.id,
            title: participant.name,
          }));
          setEventParticipants(eventParticipantsList);

          // 2. SEGUNDO: Crear lista de participantes únicos que NO están vinculados al evento
          const eventParticipantIds = new Set(
            eventParticipantsList.map((p) => p.id)
          );

          // Crear Map de participantes únicos (sin duplicados por ID)
          const uniqueParticipantsMap = new Map();
          response.Items.forEach((participant) => {
            // Solo agregar si NO está en el evento actual y NO está duplicado
            if (
              !eventParticipantIds.has(participant.id) &&
              !uniqueParticipantsMap.has(participant.id)
            ) {
              uniqueParticipantsMap.set(participant.id, {
                ...participant,
                key: participant.id,
                title: participant.name,
              });
            }
          });

          const availableParticipants = Array.from(
            uniqueParticipantsMap.values()
          );
          setAllParticipants(availableParticipants);
        } else {
          // Si no hay record, mostrar todos los participantes únicos
          const uniqueParticipantsMap = new Map();
          response.Items.forEach((participant) => {
            if (!uniqueParticipantsMap.has(participant.id)) {
              uniqueParticipantsMap.set(participant.id, {
                ...participant,
                key: participant.id,
                title: participant.name,
              });
            }
          });
          setAllParticipants(Array.from(uniqueParticipantsMap.values()));
          setEventParticipants([]);
        }
      }
    } catch (error) {
      console.error("Error al obtener participantes:", error);
    }
  }, [user?.idToken, record]);

  useEffect(() => {
    if (open) {
      obtenerParticipantes();
    }
  }, [open, obtenerParticipantes]);

  return (
    <>
      <Modal
        title={
          <PageHeader
            title={`Gestión de implicados ${record?.id || ""}`}
            head=""
          ></PageHeader>
        }
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => {
          if (onSelect && typeof onSelect === "function") {
            onSelect(eventParticipants.map((p) => p.id));
          } else {
            message.success("Participantes guardados exitosamente");
            setOpen(false);
          }
        }}
        okButtonProps={{ disabled: !record || !record.id }}
        okText="Guardar Selección"
        cancelText="Cancelar"
        width={1400}
      >
        <Row gutter={[16, 16]}>
          {/* Columna izquierda: Participantes del evento */}
          <Col span={12}>
            <Card
              title={
                <Space>
                  <UserOutlined />
                  <span>Participantes del Evento</span>
                  <Badge count={eventParticipants.length} />
                </Space>
              }
              size="small"
              style={{ height: "500px" }}
            >
              {eventParticipants.length === 0 ? (
                <Empty
                  description="No hay participantes en este evento"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {eventParticipants.map((participant) => (
                    <Card
                      key={participant.id}
                      size="small"
                      style={{ marginBottom: 8 }}
                      actions={[
                        <Tooltip title="Remover del evento">
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() =>
                              handleRemoveParticipant(participant.id)
                            }
                          />
                        </Tooltip>,
                      ]}
                    >
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <div>
                          <Text strong>{participant.name}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            ID: {participant.id}
                          </Text>
                        </div>
                        {participant.role && (
                          <Tag color="blue" style={{ margin: 0 }}>
                            {participant.role}
                          </Tag>
                        )}
                      </Space>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </Col>

          {/* Columna derecha: Búsqueda y selección */}
          <Col span={12}>
            <Card
              title={
                <Space>
                  <SearchOutlined />
                  <span>Buscar y Agregar Participantes</span>
                </Space>
              }
              size="small"
              style={{ height: "500px" }}
              extra={
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                  disabled={!record || !record.id}
                >
                  Crear Nuevo
                </Button>
              }
            >
              <div style={{ marginBottom: 16 }}>
                <Input.Search
                  placeholder="Buscar por nombre o ID..."
                  onSearch={handleSearch}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                />
              </div>

              <div style={{ maxHeight: "350px", overflowY: "auto" }}>
                {getFilteredParticipants().length === 0 ? (
                  <Empty
                    description="No se encontraron participantes"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  getFilteredParticipants().map((participant) => (
                    <Card
                      key={participant.id}
                      size="small"
                      style={{ marginBottom: 8 }}
                      actions={[
                        <Tooltip title="Agregar al evento">
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => handleAddParticipant(participant)}
                            disabled={eventParticipants.find(
                              (p) => p.id === participant.id
                            )}
                          >
                            Agregar
                          </Button>
                        </Tooltip>,
                      ]}
                    >
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <div>
                          <Text strong>{participant.name}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            ID: {participant.id}
                          </Text>
                        </div>
                        {participant.role && (
                          <Tag color="green" style={{ margin: 0 }}>
                            {participant.role}
                          </Tag>
                        )}
                        {participant.nationality && (
                          <Text type="secondary" style={{ fontSize: "11px" }}>
                            Nacionalidad: {participant.nationality}
                          </Text>
                        )}
                      </Space>
                    </Card>
                  ))
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </Modal>

      <Modal
        title={
          <Space>
            <UserAddOutlined />
            <span>Crear Nuevo Participante</span>
          </Space>
        }
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="Crear y Agregar"
        cancelText="Cancelar"
        confirmLoading={creatingParticipant}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateParticipant}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="id"
                label="ID del Participante"
                rules={[{ required: true, message: "Es requerido el ID" }]}
              >
                <Input placeholder="Ej: CC12345678" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Nombre Completo"
                rules={[{ required: true, message: "Ingresa un nombre" }]}
              >
                <Input placeholder="Nombre y apellidos" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="gender"
                label="Género"
                rules={[{ required: true, message: "Selecciona un género" }]}
              >
                <Select placeholder="Selecciona género">
                  <Option value="M">Masculino</Option>
                  <Option value="F">Femenino</Option>
                  <Option value="O">Otro</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="nationality"
                label="Nacionalidad"
                rules={[{ required: true, message: "Ingresa la nacionalidad" }]}
              >
                <Input placeholder="Ej: CO" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="role"
            label="Rol en el Evento"
            rules={[{ required: true, message: "Selecciona un rol" }]}
          >
            <Select placeholder="Selecciona el rol del participante">
              <Option value="agresor">Agresor</Option>
              <Option value="victima">Víctima</Option>
              <Option value="testigo">Testigo</Option>
              <Option value="otro">Otro</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="features"
            label="Características Físicas"
            rules={[{ required: true, message: "Ingresa las características" }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Descripción física: estatura, complexión, color de cabello, etc."
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ParticipantModal;
